import type { MouseEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { Box, useTheme, IconButton, Divider, Select, MenuItem, type SelectChangeEvent } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";

import NativePuckTitleField from "./NativePuckTitleField";
import { RichTextLinkBar } from "./RichTextLinkBar";
import { FontSize } from "./extensions/FontSize";
import { TokenSuggestion } from "./extensions/typingSuggestions/TokenSuggestion";
import { menuBarSx, menuButtonSx, editorContainerSx, dropdownSx, dropdownMenuProps } from "./richTextStyles";

const HEADING_LEVELS = [1, 2, 3, 4] as const;
type HeadingLevel = (typeof HEADING_LEVELS)[number];
type HeadingValue = "paragraph" | `${HeadingLevel}`;

const FONT_SIZE_PRESETS_PX = [12, 14, 16, 18, 20, 24, 32, 48] as const;
const DEFAULT_FONT_SIZE_VALUE = "default";

// Prevent the click from blurring the TipTap editor. Without this, clicking a
// toolbar control fires the editor's onBlur, which calls onChange, which
// propagates to Puck and re-renders this field.
const swallowMouseDown = (e: MouseEvent<HTMLElement>) => e.preventDefault();

type RichTextEditorToolbarProps = {
    editor: ReturnType<typeof useEditor>;
    readOnly?: boolean;
    isLinkBarOpen: boolean;
    onToggleLinkBar: () => void;
};

// Top-level component (NOT nested inside RichTextEditorField) so React keeps
// it mounted across parent re-renders. A nested component would be a new
// function reference on every render — React would unmount the previous
// instance and mount a fresh one, which destroys any open Select menu mid-
// click and forces the user to double-click dropdowns. This is the deeper fix
// behind the "double-click to open dropdown" bug; the swallowMouseDown above
// is the second layer of defense (preventing focus loss in the first place).
const RichTextEditorToolbar = (props: RichTextEditorToolbarProps) => {
    const { editor, readOnly, isLinkBarOpen, onToggleLinkBar } = props;
    const theme = useTheme();

    if (!editor || readOnly) return null;

    const handleBold = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleBold().run();
    };

    const handleItalic = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleItalic().run();
    };

    const handleStrike = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleStrike().run();
    };

    const handleToggleLinkBar = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onToggleLinkBar();
    };

    const handleBulletList = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleBulletList().run();
    };

    const handleOrderedList = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleOrderedList().run();
    };

    const handleInsertToken = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().insertContent("{").run();
    };

    const currentHeadingValue: HeadingValue = (() => {
        for (const level of HEADING_LEVELS) {
            if (editor.isActive("heading", { level })) return `${level}`;
        }
        return "paragraph";
    })();

    const handleHeadingChange = (event: SelectChangeEvent<HeadingValue>) => {
        const value = event.target.value as HeadingValue;
        if (value === "paragraph") {
            editor.chain().focus().setParagraph().run();
            return;
        }
        const level = Number(value) as HeadingLevel;
        editor.chain().focus().setHeading({ level }).run();
    };

    const currentFontSizeValue: string = (() => {
        const fontSize = editor.getAttributes("textStyle").fontSize as string | undefined;
        if (!fontSize) return DEFAULT_FONT_SIZE_VALUE;
        const match = FONT_SIZE_PRESETS_PX.find((px) => `${px}px` === fontSize);
        return match ? `${match}` : DEFAULT_FONT_SIZE_VALUE;
    })();

    const handleFontSizeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (value === DEFAULT_FONT_SIZE_VALUE) {
            editor.chain().focus().setMark("textStyle", { fontSize: null }).run();
            return;
        }
        editor
            .chain()
            .focus()
            .setMark("textStyle", { fontSize: `${value}px` })
            .run();
    };

    return (
        <Box sx={(theme) => menuBarSx(theme)}>
            <Select<HeadingValue>
                value={currentHeadingValue}
                onChange={handleHeadingChange}
                onMouseDown={swallowMouseDown}
                size="small"
                variant="standard"
                disableUnderline
                title="Text style"
                aria-label="Text style"
                MenuProps={dropdownMenuProps}
                sx={[(theme) => dropdownSx(theme), { minWidth: 110 }]}
            >
                <MenuItem value="paragraph">{"Paragraph"}</MenuItem>
                {HEADING_LEVELS.map((level) => (
                    <MenuItem key={level} value={`${level}`}>
                        {`Heading ${level}`}
                    </MenuItem>
                ))}
            </Select>

            <Select<string>
                value={currentFontSizeValue}
                onChange={handleFontSizeChange}
                onMouseDown={swallowMouseDown}
                size="small"
                variant="standard"
                disableUnderline
                title="Font size"
                aria-label="Font size"
                MenuProps={dropdownMenuProps}
                sx={[(theme) => dropdownSx(theme), { minWidth: 84 }]}
            >
                <MenuItem value={DEFAULT_FONT_SIZE_VALUE}>{"Default"}</MenuItem>
                {FONT_SIZE_PRESETS_PX.map((px) => (
                    <MenuItem key={px} value={`${px}`}>
                        {`${px} px`}
                    </MenuItem>
                ))}
            </Select>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <IconButton
                onClick={handleBold}
                onMouseDown={swallowMouseDown}
                title="Bold"
                size="small"
                sx={(theme) => menuButtonSx(theme, editor.isActive("bold"))}
            >
                <FormatBoldIcon fontSize="small" />
            </IconButton>
            <IconButton
                onClick={handleItalic}
                onMouseDown={swallowMouseDown}
                title="Italic"
                size="small"
                sx={(theme) => menuButtonSx(theme, editor.isActive("italic"))}
            >
                <FormatItalicIcon fontSize="small" />
            </IconButton>
            <IconButton
                onClick={handleStrike}
                onMouseDown={swallowMouseDown}
                title="Strike"
                size="small"
                sx={(theme) => menuButtonSx(theme, editor.isActive("strike"))}
            >
                <FormatStrikethroughIcon fontSize="small" />
            </IconButton>
            <IconButton
                onClick={handleToggleLinkBar}
                onMouseDown={swallowMouseDown}
                title="Add or edit link"
                aria-label="Add or edit link"
                aria-expanded={isLinkBarOpen}
                size="small"
                sx={(theme) => menuButtonSx(theme, editor.isActive("link") || isLinkBarOpen)}
            >
                <LinkIcon fontSize="small" />
            </IconButton>

            <Divider orientation="vertical" flexItem sx={{ mx: theme.spacing(0.5) }} />

            <IconButton
                onClick={handleBulletList}
                onMouseDown={swallowMouseDown}
                title="Bullet List"
                size="small"
                sx={(theme) => menuButtonSx(theme, editor.isActive("bulletList"))}
            >
                <FormatListBulletedIcon fontSize="small" />
            </IconButton>
            <IconButton
                onClick={handleOrderedList}
                onMouseDown={swallowMouseDown}
                title="Numbered List"
                size="small"
                sx={(theme) => menuButtonSx(theme, editor.isActive("orderedList"))}
            >
                <FormatListNumberedIcon fontSize="small" />
            </IconButton>

            <Divider orientation="vertical" flexItem sx={{ mx: theme.spacing(0.5) }} />

            <IconButton
                onClick={handleInsertToken}
                onMouseDown={swallowMouseDown}
                title="Insert Token"
                size="small"
                sx={(theme) => menuButtonSx(theme)}
            >
                <CodeIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export type RichTextEditorFieldProps = {
    onChange: (value: string) => void;
    value?: string;
    label?: string;
    minHeight?: number;
    maxHeight?: number;
    readOnly?: boolean;
    enableTokenSuggestions?: boolean;
};

export const RichTextEditorField = (props: RichTextEditorFieldProps) => {
    const {
        onChange,
        value = "",
        label = "Content",
        minHeight,
        maxHeight,
        readOnly,
        enableTokenSuggestions = true,
    } = props;
    const theme = useTheme();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastUserInputRef = useRef<number>(0);
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const debouncedOnChange = (html: string) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        lastUserInputRef.current = Date.now();
        debounceTimerRef.current = setTimeout(() => {
            onChange(html);
        }, 300);
    };

    const editor = useEditor({
        editable: !readOnly,
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [...HEADING_LEVELS],
                },
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    rel: "noopener noreferrer",
                    target: "_blank",
                },
            }),
            Placeholder.configure({
                placeholder: "Write something...",
            }),
            TextStyle,
            FontSize.configure({
                types: ["textStyle"],
            }),
            ...(enableTokenSuggestions ? [TokenSuggestion] : []),
        ],
        content: value,
        editorProps: {
            attributes: {
                class: "focus-visible",
            },
        },
        onUpdate: ({ editor }) => {
            lastUserInputRef.current = Date.now();
            const html = editor.getHTML();
            debouncedOnChange(html);
        },
        onFocus: () => {
            setIsEditorFocused(true);
        },
        onBlur: ({ editor }) => {
            setIsEditorFocused(false);
            const html = editor.getHTML();
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            onChange(html);
        },
    });

    useEffect(() => {
        if (!editor) return;

        const currentContent = editor.getHTML();
        const timeSinceLastInput = Date.now() - lastUserInputRef.current;

        if (currentContent !== value && (!isEditorFocused || timeSinceLastInput > 500)) {
            editor.commands.setContent(value);
        }
    }, [value, editor, isEditorFocused]);

    // Re-render the toolbar whenever the editor selection or document changes,
    // so the heading and font-size dropdowns reflect the current cursor state.
    const [, setToolbarTick] = useState(0);
    useEffect(() => {
        if (!editor) return;
        const handler = () => setToolbarTick((tick) => tick + 1);
        editor.on("selectionUpdate", handler);
        editor.on("transaction", handler);
        return () => {
            editor.off("selectionUpdate", handler);
            editor.off("transaction", handler);
        };
    }, [editor]);

    const [isLinkBarOpen, setIsLinkBarOpen] = useState(false);
    const closeLinkBar = () => setIsLinkBarOpen(false);

    const toggleLinkBar = () => setIsLinkBarOpen((prev) => !prev);

    return (
        <Box>
            <Box sx={{ mb: theme.spacing(1), pl: theme.spacing(0.5) }}>
                <NativePuckTitleField label={label} icon="edit" />
            </Box>

            <Box>
                <RichTextEditorToolbar
                    editor={editor}
                    readOnly={readOnly}
                    isLinkBarOpen={isLinkBarOpen}
                    onToggleLinkBar={toggleLinkBar}
                />
                <Box sx={(theme) => editorContainerSx(theme, { minHeight, maxHeight })}>
                    <RichTextLinkBar editor={editor} open={isLinkBarOpen} onClose={closeLinkBar} />
                    <EditorContent editor={editor} />
                </Box>
            </Box>
        </Box>
    );
};

export default RichTextEditorField;
